import { type MouseEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';

import { apiErrorContent } from '@/components/atoms/ApiErrorMessage';
import { Button } from '@/components/atoms/Buttons';
import FormField from '@/components/atoms/FormField';
import Select from '@/components/atoms/Select';
import TextField from '@/components/atoms/TextField';
import { ModalBody, ModalComponent, ModalFooter } from '@/components/molecules/ModalComponent';
import { ToastError } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { useAddClientLicense } from '@/domain/licenses';

const DEFAULT_LICENSE_NUMBER = 50;

interface AddLicenseProps {
    onClose: () => void;
    clientId: number;
    missingLicenses: {
        id: number;
        name: string;
    }[];
}

function AddLicense({ onClose, clientId, missingLicenses }: AddLicenseProps) {
    const { t } = useTranslation();
    const addClientLicense = useAddClientLicense();
    const queryClient = useQueryClient();

    const [licenseNumber, setLicenseNumber] = useState(DEFAULT_LICENSE_NUMBER);
    const [licenseSelect, setLicenseSelect] = useState(missingLicenses[0]);

    const onSubmit = async (event: MouseEvent<HTMLButtonElement>) => {
        event.preventDefault();
        const input = {
            clientId: clientId,
            licenseId: licenseSelect.id,
            numLicenses: licenseNumber,
        };

        try {
            await addClientLicense.mutateAsync(input);
            onClose();
        } catch (error: unknown) {
            // The dialog stays open with what was entered, so the user can correct it.
            ToastError(
                apiErrorContent(error, t('internal.clientLicense.addFailed')),
                'client-license-add-error'
            );
        } finally {
            queryClient.invalidateQueries();
        }
    };

    return (
        <ModalComponent
            isOpen={true}
            onClose={onClose}
            centered={false}
            title={t('internal.client.addClientLicense')}>
            <ModalBody>
                <FormField label={t('internal.clientLicense.license')}>
                    <Select
                        name="license"
                        value={JSON.stringify(licenseSelect)}
                        onChange={(e) => {
                            setLicenseSelect(JSON.parse(e.target.value));
                        }}>
                        {missingLicenses.map((license) => (
                            <option key={license.id} value={JSON.stringify(license)}>
                                {license.name}
                            </option>
                        ))}
                    </Select>
                </FormField>
                <FormField label={t('internal.client.numberOfLicense')}>
                    <TextField
                        type="text"
                        name="licenseNumber"
                        placeholder=""
                        value={licenseNumber}
                        onChange={(e) => {
                            !Number.isNaN(Number(e.target.value)) &&
                                setLicenseNumber(Number(e.target.value));
                        }}
                    />
                </FormField>
            </ModalBody>
            <ModalFooter>
                <Button variant="secondary" onClick={onClose}>
                    {t('common.actions.cancel')}
                </Button>
                <Button variant="primary" onClick={onSubmit}>
                    {t('common.actions.save')}
                </Button>
            </ModalFooter>
        </ModalComponent>
    );
}

export default AddLicense;
